import React, { useCallback, useMemo, useState } from 'react';
import { Icon, IconsEnum } from 'components/Icons';
import { breakpoint, isHappybetSite } from 'utility/constant';
import { useAppDispatch } from 'lib/centralStore';
import { useGetLabelsByKeys, useTranslation } from 'hooks/useLingUI';

import { ButtonField } from 'components/mieScommesseTableRow/buttonField/ButtonField';
import { Spinner } from 'components/spinner';
import { getAmount } from 'utility/functions';
import { getWallet } from 'features/dashboard/action';
import styles from './CashOutButton.module.scss';
import { usePostCashoutTotaleMutation } from 'features/api/userSlice';
import useWindowSize, { enComparer } from 'hooks/useWindowSize';

export type CashOutButtonProps = {
  label: string;
  importo: number;
  isBlocked: boolean;
  isInColumn: boolean;
  idBiglietto: string;
  internalClassName?: string;
  onClick?: () => void;
  onError?: () => void;
  onSuccess?: () => void;
  refetchApiBets?: () => void;
};

export const CashOutButton = ({
  label,
  importo,
  isBlocked,
  isInColumn,
  idBiglietto,
  internalClassName,
  onClick,
  onError,
  onSuccess,
  refetchApiBets,
}: CashOutButtonProps) => {
  const { t } = useTranslation();
  const { doCheck } = useWindowSize();

  const [isMobile] = doCheck([{ comparer: enComparer.less, size: breakpoint.sm }]);

  const dispatch = useAppDispatch();
  const [cashout, { error: errorCashoutTotale, isLoading }] = usePostCashoutTotaleMutation();

  const [error, setError] = useState(false);
  const [success, setSuccess] = useState(false);

  const [labelCashoutInProgress, labelCashoutCompletedPart1, labelCashoutCompletedPart2] = useGetLabelsByKeys([
    'cashout-in-progress',
    'cashout-completed-part1',
    'cashout-completed-part2',
  ]);

  const handleClick = useCallback(() => {
    if (typeof onClick === 'function') {
      onClick();
    }

    if (isBlocked) {
      cashout({ importo, idBiglietto })
        .unwrap()
        .then(() => {
          dispatch(getWallet({ ts: new Date() }));
          setSuccess(true);
          setTimeout(() => {
            onSuccess && onSuccess();
            setSuccess(false);
            refetchApiBets && refetchApiBets();
          }, 2000);
        })
        .catch(() => {
          setError(true);
          setTimeout(() => {
            onError && onError();
            setError(false);
          }, 2000);
        });
    }
  }, [isBlocked, importo, idBiglietto, onClick, cashout, dispatch, onSuccess, refetchApiBets, onError]);

  const formatter = new Intl.NumberFormat('it-IT', {
    style: 'currency',
    currency: 'EUR',

    // These options are needed to round to whole numbers if that's what you want.
    //minimumFractionDigits: 0, // (this suffices for whole numbers, but will print 2500.10 as $2,500.1)
    maximumFractionDigits: 2, // (causes 2500.99 to be printed as $2,501)
  });

  const errDescription = useMemo(() => {
    if (!errorCashoutTotale) return undefined;
    const response = Reflect.get((errorCashoutTotale as any)?.data ?? {}, 'response') ?? {};
    const { esito, descrizione } = response ?? {};
    const label = t(`cashout-error-${esito}`, `#cashout-error-${esito} - ${descrizione}`);
    return isHappybetSite ? label : descrizione;
  }, [errorCashoutTotale, t]);

  return isLoading ? (
    <div className={`${styles.loading} ${isMobile || isInColumn ? styles.containerMobile : ''}`}>
      <Spinner className={styles.spinner} />
      {labelCashoutInProgress}
    </div>
  ) : success ? (
    <div className={`${styles.completed} ${isMobile || isInColumn ? styles.containerMobile : ''}`}>
      <Icon iconId={IconsEnum.CHECK} className={styles.icon} />
      <span>{`${labelCashoutCompletedPart1} ${formatter.format(
        getAmount(importo)
      )} ${labelCashoutCompletedPart2}`}</span>
    </div>
  ) : error ? (
    <div className={`${styles.error} ${isMobile || isInColumn ? styles.containerMobile : ''}`}>
      <Icon iconId={IconsEnum.CLOSE_CIRCLE} className={styles.icon} />
      <span>{errDescription}</span>
    </div>
  ) : (
    <ButtonField label={label} onClick={handleClick} isInColumn={isInColumn} internalClassName={internalClassName} />
  );
};